import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Package,
  Warehouse,
  MapPin,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  ArrowRight,
  ArrowRightLeft,
  Boxes,
  ClipboardList,
  Cpu,
  Sparkles,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { productsApi } from '@/api/productsApi';
import { locationsApi } from '@/api/locationsApi';
import { salesApi } from '@/api/salesApi';
import { inventoryApi } from '@/api/inventoryApi';
import { collectionsApi } from '@/api/collectionsApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { EntityLink } from '@/components/detail/EntityLink';

// ─── Chart helpers ─────────────────────────────────────────────────────────────

const AMBER = '#F59E0B';
const CHART_COLORS = ['#F59E0B', '#60A5FA', '#A78BFA', '#34D399', '#F87171', '#FB923C'];

function buildRevenueData(sales: any[] = []) {
  const map = new Map<string, number>();
  sales.forEach((s) => {
    try {
      const label = format(parseISO(s.saleDate), 'MMM d');
      map.set(label, (map.get(label) || 0) + (s.totalSaleAmount || 0));
    } catch {
      // skip bad dates
    }
  });
  return Array.from(map.entries()).map(([date, revenue]) => ({ date, revenue }));
}

function buildLocationData(inventory: any[] = []) {
  const map = new Map<string, number>();
  inventory.forEach((item) => {
    const loc = item.locationName || 'Unknown';
    map.set(loc, (map.get(loc) || 0) + (item.quantityOnShelf || 0));
  });
  return Array.from(map.entries())
    .map(([location, quantity]) => ({ location, quantity }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 8);
}

// Custom tooltip styled to match the theme
function ChartTooltip({ active, payload, label, formatter }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-gray-500 dark:text-warm-sand mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="font-semibold text-gray-900 dark:text-warm-cream">
          {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

// ─── Dashboard ─────────────────────────────────────────────────────────────────

export function Dashboard() {
  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => collectionsApi.getAll(false),
  });

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.getAll,
  });

  const { data: sales, isLoading: salesLoading } = useQuery({
    queryKey: ['sales'],
    queryFn: salesApi.getAll,
  });

  const { data: lowStockItems, isLoading: lowStockLoading } = useQuery({
    queryKey: ['low-stock'],
    queryFn: inventoryApi.getLowStock,
  });

  const { data: allInventory } = useQuery({
    queryKey: ['inventory'],
    queryFn: inventoryApi.getAll,
  });

  const { data: unpaidCommissions } = useQuery({
    queryKey: ['unpaid-commissions'],
    queryFn: salesApi.getTotalUnpaidCommissions,
  });

  const totalRevenue = sales?.reduce((sum, s) => sum + (s.totalSaleAmount || 0), 0) || 0;
  const recentSales = sales?.slice(0, 5) || [];

  const revenueChartData = buildRevenueData(sales);
  const locationChartData = buildLocationData(allInventory as any[]);

  const stats = [
    {
      title: 'Total Products',
      value: products?.length || 0,
      icon: Package,
      color: 'text-amber-450',
      bgColor: 'bg-amber-450/10',
    },
    {
      title: 'Locations',
      value: locations?.length || 0,
      icon: MapPin,
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: '3D Printed Items',
      value: products?.filter(p => p.is3dPrinted).length || 0,
      icon: Warehouse,
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(totalRevenue),
      icon: DollarSign,
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-500/10',
    },
  ];

  const collectionCounts = (collections ?? []).map((collection) => ({
    ...collection,
    count:
      products?.filter((p) => {
        const ext = p as { collectionId?: string | null };
        return ext.collectionId === collection.id;
      }).length ?? 0,
  }));

  const quickActions = [
    {
      title: 'Receive Supplies',
      description: 'I just got a Temu or Amazon package.',
      to: '/supplies',
      icon: Boxes,
      className: 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300',
    },
    {
      title: 'Record Production',
      description: 'I just printed, engraved, or made a batch.',
      to: '/production',
      icon: Cpu,
      className: 'border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-300 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300',
    },
    {
      title: 'Take Inventory',
      description: "I'm counting what is on hand.",
      to: '/inventory',
      icon: ClipboardList,
      className: 'border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-300 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-300',
    },
    {
      title: 'Transfer Inventory',
      description: "I'm moving products to another location.",
      to: '/transfers',
      icon: ArrowRightLeft,
      className: 'border-stone-200 bg-stone-50 text-stone-700 hover:border-stone-300 dark:border-stone-500/20 dark:bg-stone-500/10 dark:text-stone-300',
    },
    {
      title: 'Record Sale',
      description: 'I sold something.',
      to: '/sales',
      icon: DollarSign,
      className: 'border-yellow-200 bg-yellow-50 text-yellow-800 hover:border-yellow-300 dark:border-yellow-500/20 dark:bg-yellow-500/10 dark:text-yellow-300',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-sans text-3xl font-bold text-gray-900 dark:text-warm-cream">What would you like to do today?</h1>
        <p className="text-gray-600 dark:text-warm-sand mt-1">The dashboard is your workbench. Pick the action that matches your day.</p>
      </div>

      <div className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-amber-450" />
          <h2 className="font-sans text-xl font-semibold text-gray-900 dark:text-warm-cream">
            Start Here
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {quickActions.map((action) => (
            <Link
              key={action.title}
              to={action.to}
              className={`rounded-2xl border p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${action.className}`}
            >
              <action.icon className="w-6 h-6 mb-3" />
              <p className="font-semibold text-gray-900 dark:text-warm-cream">{action.title}</p>
              <p className="mt-1 text-xs text-gray-600 dark:text-warm-sand">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {collectionCounts.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-450" />
            <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">
              Browse by Collection
            </h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-warm-sand mb-4">
            What do we have for Pride, Bear Gear, Pups, and other themed lines?
          </p>
          <div className="flex flex-wrap gap-2">
            {collectionCounts.map((collection) => (
              <Link
                key={collection.id}
                to={`/products?collection=${collection.id}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 hover:border-amber-450/50 transition-colors text-sm"
              >
                <span>{collection.icon || '✨'}</span>
                <span className="font-medium text-gray-900 dark:text-warm-cream">{collection.name}</span>
                <span className="text-xs text-gray-500 dark:text-warm-sand/60">
                  {collection.count} item{collection.count !== 1 ? 's' : ''}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card p-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-warm-sand">{stat.title}</p>
                <p className="font-sans text-2xl font-bold text-gray-900 dark:text-warm-cream">
                  {productsLoading || locationsLoading || salesLoading ? '-' : stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Charts ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Over Time */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-5">
            <TrendingUp className="w-5 h-5 text-amber-450" />
            <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">Revenue Over Time</h2>
          </div>
          {salesLoading ? (
            <div className="h-[200px] flex items-center justify-center text-gray-600 dark:text-warm-sand text-sm">
              Loading…
            </div>
          ) : revenueChartData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-gray-600 dark:text-warm-sand text-sm">
              No sales data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={revenueChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={AMBER} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={AMBER} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `$${v}`}
                  width={48}
                />
                <Tooltip
                  content={
                    <ChartTooltip formatter={(v: number) => formatCurrency(v)} />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={AMBER}
                  strokeWidth={2}
                  fill="url(#revenueGradient)"
                  dot={false}
                  activeDot={{ r: 4, fill: AMBER, stroke: '#1C1917', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Inventory by Location */}
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-5">
            <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">Inventory by Location</h2>
          </div>
          {locationChartData.length === 0 ? (
            <div className="h-[200px] flex items-center justify-center text-gray-600 dark:text-warm-sand text-sm">
              No inventory data yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={locationChartData}
                margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
                barCategoryGap="30%"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal vertical={false} />
                <XAxis
                  dataKey="location"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={40}
                />
                <YAxis
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={32}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="quantity" radius={[4, 4, 0, 0]}>
                  {locationChartData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={CHART_COLORS[index % CHART_COLORS.length]}
                      fillOpacity={0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Alerts & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Low Stock Alerts */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-charcoal-700">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-450" />
              <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">
                Low Stock Alerts
              </h2>
              <span className="badge-warning ml-auto">
                {lowStockItems?.length || 0} items
              </span>
            </div>
          </div>
          <div className="p-4">
            {lowStockLoading ? (
              <p className="text-gray-600 dark:text-warm-sand">Loading...</p>
            ) : lowStockItems?.length === 0 ? (
              <p className="text-gray-600 dark:text-warm-sand">No low stock items. Great job!</p>
            ) : (
              <div className="space-y-3">
                {lowStockItems?.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-charcoal-700/50 rounded-lg">
                    <div>
                      <p className="font-medium mono text-sm text-gray-900 dark:text-warm-cream">{item.variantSku}</p>
                      <EntityLink type="location" id={item.locationId} label={item.locationName} className="text-sm font-normal" />
                    </div>
                    <span className="badge-danger">
                      {item.quantityOnShelf} left
                    </span>
                  </div>
                ))}
                {lowStockItems && lowStockItems.length > 5 && (
                  <Link to="/inventory" className="text-amber-450 text-sm hover:underline flex items-center gap-1">
                    View all {lowStockItems.length} items <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Sales */}
        <div className="card">
          <div className="p-4 border-b border-gray-200 dark:border-charcoal-700">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h2 className="font-sans text-lg font-semibold text-gray-900 dark:text-warm-cream">
                Recent Sales
              </h2>
            </div>
          </div>
          <div className="p-4">
            {salesLoading ? (
              <p className="text-gray-600 dark:text-warm-sand">Loading...</p>
            ) : recentSales.length === 0 ? (
              <p className="text-gray-600 dark:text-warm-sand">No sales recorded yet.</p>
            ) : (
              <div className="space-y-3">
                {recentSales.map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between p-3 bg-gray-100 dark:bg-charcoal-700/50 rounded-lg">
                    <div>
                      <EntityLink type="product" id={sale.productId} label={sale.productName} />
                      <p className="text-sm text-gray-600 dark:text-warm-sand flex flex-wrap items-center gap-1">
                        <EntityLink type="location" id={sale.locationId} label={sale.locationName} className="text-sm font-normal" />
                        <span>• {formatDate(sale.saleDate)}</span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900 dark:text-warm-cream">{formatCurrency(sale.totalSaleAmount)}</p>
                      <p className="text-xs text-gray-600 dark:text-warm-sand">Qty: {sale.quantity}</p>
                    </div>
                  </div>
                ))}
                {sales && sales.length > 5 && (
                  <Link to="/sales" className="text-amber-450 text-sm hover:underline flex items-center gap-1">
                    View all sales <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unpaid Commissions Alert */}
      {unpaidCommissions && unpaidCommissions > 0 && (
        <div className="card p-4 border border-amber-450/30 bg-amber-450/5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-amber-450" />
              <div>
                <p className="font-medium text-gray-900 dark:text-warm-cream">Unpaid Commissions</p>
                <p className="text-sm text-gray-600 dark:text-warm-sand">
                  You have {formatCurrency(unpaidCommissions)} in unpaid commissions to locations
                </p>
              </div>
            </div>
            <Link to="/sales" className="btn-primary text-sm">
              View Details
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
